// @flow
import React from 'react';
import styled from 'styled-components';
import { ComboBoxInput, Button, type ComboBoxOptions } from 'bench-internal-theme';
import type { SuggestedLedger } from './types';

const maxCategorizationSuggestions = 3;

const getAvailableLedgerOptions = (selectedLedgerSuggestions, ledgerOptions) => {
  if (!selectedLedgerSuggestions) {
    return ledgerOptions;
  }

  const selectedLedgerAccountIds = selectedLedgerSuggestions.map(
    ledgerSuggestion => ledgerSuggestion.ledgerAccount.id,
  );
  return ledgerOptions.filter(
    ledgerOption => !selectedLedgerAccountIds.includes(ledgerOption.value),
  );
};

const handleEditOrDelete = (
  suggestedLedger,
  ledgerAccountOption,
  onDeleteCategorization,
  onEditCategorization,
) => {
  if (!ledgerAccountOption && typeof onDeleteCategorization === 'function') {
    onDeleteCategorization(suggestedLedger);
  }

  if (ledgerAccountOption && typeof onEditCategorization === 'function') {
    onEditCategorization(suggestedLedger, ledgerAccountOption.value);
  }
};

const addCategorization = (ledgerAccountOption, onAddCategorization) => {
  if (ledgerAccountOption && typeof onAddCategorization === 'function') {
    onAddCategorization(ledgerAccountOption.value);
  }
};

const acceptCategorization = (suggestedLedger, onAcceptCategorization) => {
  if (typeof onAcceptCategorization === 'function') {
    onAcceptCategorization(suggestedLedger);
  }
};

const CategorizationSuggestionsLayout = styled.div`
  display: grid;
  grid-row-gap: 4px;
`;

const AssignButton = styled(Button)`
  opacity: 0;

  &:focus {
    opacity: 1;
  }
`;

const CategorizationSuggestionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 24px;
  align-items: center;

  &:hover {
    ${AssignButton} {
      opacity: 1;
    }
  }
`;

type CategorizationSuggestionsProps = {
  defaultCategorizationSuggestions?: Array<SuggestedLedger>,
  ledgerOptions: ComboBoxOptions,
  onAcceptCategorization?: (suggestedLedger: SuggestedLedger) => void,
  onAddCategorization?: (ledgerAccountId: string) => void,
  onDeleteCategorization?: (suggestedLedger: SuggestedLedger) => void,
  onEditCategorization?: (suggestedLedger: SuggestedLedger, ledgerAccountId: string) => void,
};

const CategorizationSuggestions = ({
  defaultCategorizationSuggestions,
  ledgerOptions,
  onAcceptCategorization,
  onAddCategorization,
  onDeleteCategorization,
  onEditCategorization,
}: CategorizationSuggestionsProps) => {
  const commonInputProps = {
    isClearable: true,
    isSearchable: true,
    maxMenuHeight: 200,
    options: getAvailableLedgerOptions(defaultCategorizationSuggestions, ledgerOptions),
    size: 'small',
    menuPortalTarget: document.body,
  };

  const categorizationSuggestions = defaultCategorizationSuggestions || [];

  return (
    <CategorizationSuggestionsLayout>
      {categorizationSuggestions.map(ledgerSuggestion => (
        <CategorizationSuggestionRow key={ledgerSuggestion.id}>
          <ComboBoxInput
            defaultValue={ledgerOptions.find(
              option => option.value === ledgerSuggestion.ledgerAccount.id,
            )}
            onChange={ledgerAccountOption =>
              handleEditOrDelete(
                ledgerSuggestion,
                ledgerAccountOption,
                onDeleteCategorization,
                onEditCategorization,
              )
            }
            {...commonInputProps}
          />
          <AssignButton
            icon="check"
            onClick={() => acceptCategorization(ledgerSuggestion, onAcceptCategorization)}
            size="small"
            styleType="total-ghost"
          />
        </CategorizationSuggestionRow>
      ))}
      {categorizationSuggestions.length < maxCategorizationSuggestions && (
        <CategorizationSuggestionRow>
          <ComboBoxInput
            value=""
            onChange={ledgerOption => addCategorization(ledgerOption, onAddCategorization)}
            {...commonInputProps}
          />
        </CategorizationSuggestionRow>
      )}
    </CategorizationSuggestionsLayout>
  );
};

CategorizationSuggestions.defaultProps = {
  defaultCategorizationSuggestions: undefined,
  onAcceptCategorization: undefined,
  onAddCategorization: undefined,
  onDeleteCategorization: undefined,
  onEditCategorization: undefined,
};

export default CategorizationSuggestions;
